/**
 * Rating + distance maths shared by the clinic directory and the SEO location
 * pages. Lives here (not in a page) so a page import never drags a route's
 * whole bundle into another route.
 */

export interface Review {
  user?: string;
  comment?: string;
  rating?: number;
  /** ISO date; set on reviews written by customers after a booking. */
  date?: string;
  verified?: boolean;
  /** Where the review was written. Imported Google reviews are labelled, never verified. */
  source?: 'recovry' | 'google';
}

/**
 * Reviews that may back an aggregateRating in schema: written on Recovry after
 * a booking. Google's review-snippet policy excludes third-party and unverifiable
 * reviews, so imported and legacy embedded reviews stay display-only.
 */
export function firstPartyReviews<T extends Review>(reviews: T[]): T[] {
  return reviews.filter((r) => r.verified === true);
}

interface RatedService {
  name?: string;
  reviews?: Review[];
}

interface ReviewedCentre {
  services?: RatedService[];
  reviews?: Review[];
}

/** A review tagged with the service it was left on; clinic-level reviews have none. */
export type ReviewWithService = Review & { serviceName?: string };

/** Service reviews plus clinic-level ones; `serviceName` is undefined for the latter. */
export function allCentreReviews<T extends ReviewedCentre>(centre: T): ReviewWithService[] {
  const fromServices = (centre.services ?? []).flatMap((s) =>
    (s.reviews ?? []).map((r) => ({ ...r, serviceName: s.name })),
  );
  return [...fromServices, ...(centre.reviews ?? [])];
}

/** Mean rating across reviews, or null when nothing is rated yet. */
export function calcRating(reviews?: Review[]): number | null {
  if (!reviews || reviews.length === 0) return null;
  const valid = reviews.filter((r) => typeof r.rating === 'number');
  if (valid.length === 0) return null;
  return valid.reduce((sum, r) => sum + (r.rating as number), 0) / valid.length;
}

/** A centre's rating is the mean over all its reviews, service-level and clinic-level. */
export function centreRating(centre: ReviewedCentre): number | null {
  return calcRating(allCentreReviews(centre));
}

/** Great-circle distance in km. */
export function getDistanceKm(lat1: number, lng1: number, lat2: number, lng2: number) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
